/**
 * NOTE - Webpack can't remove this module's dependencies via tree shaking
 * Lightweight exports have been moved to /localization/pure
 */

import Polyglot from 'node-polyglot'
import moment from 'moment'
import numbro from 'numbro'
import {mapLaguageToLocalizedDateFormatter} from '../../../src/shared/helpers/translations.js'
import { is, isNil, apply, mapObjIndexed, pathOr, __ } from 'ramda'
// better way to do this?
import 'moment-timezone'
import 'moment/locale/ar'
import 'moment/locale/da'
import 'moment/locale/de'
import 'moment/locale/el'
import 'moment/locale/es'
import 'moment/locale/fi'
import 'moment/locale/fr'
import 'moment/locale/hi'
import 'moment/locale/id'
import 'moment/locale/it'
import 'moment/locale/ja' // jp in our files
import 'moment/locale/ko' // kr in our files
import 'moment/locale/nl'
import 'moment/locale/nb' // no in our files (nb is Norwegian Bokmål)
import 'moment/locale/pt'
import 'moment/locale/ru'
import 'moment/locale/sv'
import 'moment/locale/th'
import 'moment/locale/tr'
import 'moment/locale/zh-tw' // tw in our files
import 'moment/locale/zh-cn' // zh in our files

// TODO is there a way to (neatly) get only the languages we need?
import numbroLangs from 'numbro/languages'

export * from './pure'

// setup (one time)
// numbro
mapObjIndexed((value, key) => numbro.culture(key, value), numbroLangs)

/*
 * NB missing formats (default to english):
 * Hindi (hi)
 * Arabic (ar)
 */
const numbroLocale = locale => {
  switch (locale) {
    case 'da': return 'da-DK'
    case 'de': return 'de-DE'
    case 'el': return 'el'
    case 'es': return 'es-ES'
    case 'fi': return 'fi-FI'
    case 'fr': return 'fr-FR'
    case 'id': return 'id'
    case 'it': return 'it-IT'
    case 'jp': return 'ja-JP'
    case 'kr': return 'ko-KR'
    case 'nl': return 'nl-BE'
    case 'no': return 'nb-NO'
    case 'pt': return 'pt-BR'
    case 'ru': return 'ru-RU'
    case 'sv': return 'sv-SE'
    case 'th': return 'th-TH'
    case 'tr': return 'tr-TR'
    case 'tw': return 'zh-TW'
    case 'zh': return 'zh-CN'
    default: return 'en-US'
  }
}

const momentLocale = locale => {
  switch (locale) {
    case 'pt_br': return 'pt'
    case 'fr_fr': return 'fr'
    case 'fr_ca': return 'fr'
    case 'it_it': return 'it'
    case 'es_es': return 'es'
    case 'de_de': return 'de'
    case 'kr': return 'ko'
    case 'no': return 'nb'
    case 'tw': return 'zh-tw'
    case 'zh': return 'zh-cn'
    default: return locale
  }
}

const getCleanT = (locale, phrases) => {
  const polyglot = new Polyglot({
    locale,
    allowMissing: true,
    phrases,
  })

  return polyglot.t.bind(polyglot)
}

const ensureLocalizedSingleton = locale => {
  const culture = numbroLocale(locale)

  if (numbro.culture() !== culture) {
    numbro.culture(culture) // culture ? ... had to be different
  }
}

const applyIfArray = (...args) => {
  if (is(Array, args[0])) {
    return apply(__, args[0])
  }

  return apply(__, args)
}

/*
 * moment helpers
 */

const setTimeThresholdsForHumanization = () => {
  moment.relativeTimeThreshold('s', 59)
  moment.relativeTimeThreshold('m', 59)
  moment.relativeTimeThreshold('h', 23)
  moment.relativeTimeThreshold('d', 28)
  moment.relativeTimeThreshold('M', 12)
}

const resetTimeThresholdsToDefaults = () => {
  moment.relativeTimeThreshold('s', 45)
  moment.relativeTimeThreshold('m', 45)
  moment.relativeTimeThreshold('h', 22)
  moment.relativeTimeThreshold('d', 26)
  moment.relativeTimeThreshold('M', 11)
}

export const isValidTimeZone = tz => tz && !!moment.tz.zone(tz)

export const guessTimeZone = () => moment.tz.guess(true)

/**
 * Parse timezone into abbreviation
 * @param {String} tz - timezone name (tz database spec)
 */
export const parseTimeZone = tz => moment.tz(tz).zoneName()

/**
 * Guess local timezone abbreviation
 */
export const getLocalTimeZone = () => parseTimeZone(guessTimeZone())

export const localizedNumberFormatter = (locale, phrases) => {
  const t = getCleanT(locale, phrases)
  return (number, format) => {
    ensureLocalizedSingleton(locale)
    switch (format) {
      case 'payment': return t('payment.format', {
        amount: numbro(number).format('0,0.00'),
        currency: numbro.cultures()[locale].currency.symbol,
      })
      case 'decimal': return numbro(number).format('0,0[.][00]')
      default: return numbro(number).format(format)
    }
  }
}

export const localizedTranslate = (locale, phrases) => {
  const t = getCleanT(locale, phrases)

  /**
   * translate
   *
   * @param {String} key - key to lookup translation based on locale
   * @param {Object} options -
   *   count {Integer}: Set polyglot smart_count (how polyglot determines pluralization)
   *          AND make a locale formatted "count" for use in the translation
   *   markup {Object | Array}: Replace text between angle brackets with predefined HTML
   *     ex: key -> 'Hello <World>'
   *         translate(key, {markup: tagStart: '<a href="#">'})
   *         => 'Hello <a href="#">World</a>'
   *         key -< 'foo <bar> <baz>'
   *         translate(key, {markup: [{tagStart: '<b>'}, {tagStart: '<i>'}]})
   *         => 'foo <b>bar</b> <i>baz</i>'
   */

  const translate = (key, options = {}) => {
    /**
     * pre lookup formatting
     */
    if (is(Object, options)) {
      if (is(Number, options.count)) {
        options.smart_count = options.count
        options.count = numbro(options.count).format()
      }
    }
    let text = isNil(key) ? '' : t(key)

    if (text === key) {
      return text
    }

    /**
     * manipulate raw value before passing options
     */

    if (options.markup || /<.*>/.test(text)) {
      let i = 0
      let sameTag = false
      let markup = options.markup || { tagName: 'span' }

      if (is(Array, markup)) {
        // NB hack to get around the parsing limitations of <> to delimit html
        // would be ideal to replace this with better delimiters such as {{}}
        // in our translation files
        if (options.count) {
          markup = text.split('||||').reduce(memo => [...memo, ...markup], [])
        }
      } else if (is(Object, markup)) {
        markup = [markup]
        sameTag = true
      } else {
        throw new Error(`markup expected <Array|Object> received ${typeof markup}`)
      }

      text = text.replace(/<([^<>]*)>/g, (match, capture) => {
        const currMarkup = markup[sameTag ? 0 : i++]

        if (currMarkup) {
          const tagName = currMarkup.tagName || currMarkup.tagStart.replace(/<([^<> ]*)(.*)/, '$1')
          const innerText = capture || ''
          const tagStart = currMarkup.tagStart || `<${tagName}>`

          return `${tagStart}${innerText}</${tagName}>`
        }

        return match
      })
    }

    text = t(text, options)

    /**
     * post lookup formatting
     */

    return text.replace(/\n/g, '<br>')
  }

  return (key, options = {}) => {
    ensureLocalizedSingleton(locale)
    return applyIfArray(key, options)(translate)
  }
}

export const localizedDateFormatter = (icLocale, timezone) => {
  const locale = momentLocale(icLocale)
  if (!timezone) {
    timezone = guessTimeZone()
  }

  const formatDate = (date, format) => {
    const m = moment.tz(date, timezone).locale(locale)
    switch (format) {
      case 'formatDate': return new Intl.DateTimeFormat(mapLaguageToLocalizedDateFormatter(icLocale),{ weekday: 'long', month: 'long', day: 'numeric' }).format(m)
      case 'dmy': return m.format('ll')
      case 'time': return m.format('LT')
      case 'fromNow': return m.fromNow()
      default: return m.format(format)
    }
  }
  /*
   * Add other locale dependent methods here as needed
   * keep locale agnostic ones as separate exports
   */

  /*
   * compute diff between two dates with our own formatting
   * TODO - determine if this is necessary vs just using moment's `diff`
   * also - from a cursory look, probably not right for RTL langs (ar)
   */
  formatDate.diff = (date1, date2) => {
    setTimeThresholdsForHumanization()
    const duration = moment.duration(moment(date1).diff(date2)).locale(locale)
    const lessThanDay = !(duration.days() || duration.months() || duration.years())
    let humanReadableDuration = duration.humanize()

    if (lessThanDay && Math.abs(duration.hours()) > 0 && Math.abs(duration.minutes()) > 0) {
      // calculate hours & minutes separately because of library limitations
      // (https://github.com/moment/moment/issues/1048)
      const hoursDuration = moment.duration(
        duration.hours(),
        'hours'
      ).locale(locale).humanize()
      const minutesDuration = moment.duration(
        duration.minutes(),
        'minutes'
      ).locale(locale).humanize()

      humanReadableDuration = `${hoursDuration} ${minutesDuration}`
    }
    resetTimeThresholdsToDefaults()

    return humanReadableDuration
  }

  return formatDate
}

export const getUserTimeZone = pathOr('', ['user', 'userInfo', 'timeZone'])
